import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  FileDownload as DownloadIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';

interface FileViewerModalProps {
  open: boolean;
  onClose: () => void;
  transactionId: string;
  filename?: string;
  isAdmin?: boolean;
}

const FileViewerModal: React.FC<FileViewerModalProps> = ({
  open,
  onClose,
  transactionId,
  filename = 'File',
  isAdmin = false,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [fileUrl, setFileUrl] = React.useState<string | null>(null);
  const [fileType, setFileType] = React.useState<string>('');
  const { token } = useAuthStore();

  React.useEffect(() => {
    if (open && transactionId) {
      loadFile();
    } else {
      // Clean up when modal closes
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
        setFileUrl(null);
      }
    }

    return () => {
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [open, transactionId]);

  const loadFile = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!token?.access_token) {
        throw new Error('Authentication required');
      }
      // Check if this is a file ID (UUID format) or transaction ID
      const isFileId = transactionId.length === 36 && transactionId.includes('-');

      let endpoint = '';
      if (isFileId) {
        // Direct file access
        endpoint = isAdmin
          ? `/api/v1/admin/files/${transactionId}/download`
          : `/api/v1/files/${transactionId}/download`;
      } else {
        // Transaction-based file access
        endpoint = isAdmin
          ? `/api/v1/admin/transactions/${transactionId}/file`
          : `/api/v1/files/transaction/${transactionId}/file`;
      }

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token?.access_token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('File not found');
        } else if (response.status === 403) {
          throw new Error('Access denied');
        } else {
          throw new Error(`Failed to load file: ${response.statusText}`);
        }
      }

      const contentType = response.headers.get('content-type') || '';
      setFileType(contentType);

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setFileUrl(url);
    } catch (err: any) {
      setError(err.message || 'Failed to load file');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (fileUrl) {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const getFileIcon = () => {
    if (fileType.startsWith('image/')) {
      return <ImageIcon />;
    } else if (fileType === 'application/pdf') {
      return <PdfIcon />;
    } else {
      return <FileIcon />;
    }
  };

  const renderFileContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading file...</Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      );
    }

    if (!fileUrl) {
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          No file to display
        </Alert>
      );
    }

    if (fileType.startsWith('image/')) {
      return (
        <Box sx={{ textAlign: 'center', maxHeight: 600, overflow: 'auto' }}>
          <img
            src={fileUrl}
            alt={filename}
            style={{
              maxWidth: '100%',
              maxHeight: '600px',
              objectFit: 'contain',
            }}
          />
        </Box>
      );
    } else if (fileType === 'application/pdf') {
      return (
        <Box sx={{ height: 600, width: '100%' }}>
          <iframe
            src={fileUrl}
            width="100%"
            height="100%"
            style={{ border: 'none', borderRadius: 4 }}
            title={filename}
          />
        </Box>
      );
    } else {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <FileIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            File Preview Not Available
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            This file type cannot be previewed in the browser.
          </Typography>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{ mt: 2 }}
          >
            Download File
          </Button>
        </Box>
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '400px' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getFileIcon()}
            <Typography variant="h6">
              {filename}
            </Typography>
            {isAdmin && (
              <Chip label="Admin View" color="primary" size="small" />
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Transaction ID: {transactionId}
          </Typography>
          {fileType && (
            <Typography variant="body2" color="textSecondary">
              File Type: {fileType}
            </Typography>
          )}
        </Box>

        {renderFileContent()}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
        {fileUrl && !error && (
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
          >
            Download
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FileViewerModal;